import React, { useEffect, useState } from "react";
import { observable, action } from "mobx";
import { observer } from "mobx-react";
import userDetail from "Stores/userDetail";
import Utils from "Utils/utils";
import { LINKEDIN_PLUGIN_ZIP_DOWNLOAD_URL } from "Static/constant";
import "./pluginNotInstalled.scss";

class LinkedInInstallPopupState {
  @observable showPopup = false;

  @action
  setShowPopup = (val = false) => {
    this.showPopup = val;
  };
}
const linkedInInstallPopupState = new LinkedInInstallPopupState();

const getBrowser = () => {
  const ua = navigator.userAgent || "";
  if (/edg/i.test(ua)) return "edge";
  if (/chrome|chromium|crios/i.test(ua) && !/opr|opera/i.test(ua))
    return "chrome";
  return "other";
};

const INSTALL_STEPS = {
  chrome: {
    label: "Google Chrome",
    extensionsUrl: "chrome://extensions",
    steps: [
      "Extract the downloaded ZIP file to a folder on your computer.",
      { text: "Open ", link: "chrome://extensions", after: "." },
      "Turn Developer mode ON (top-right corner).",
      "Click Load unpacked (top-left corner).",
      "Browse to the extracted SalesIntel folder.",
      "Select the folder and click Select Folder."
    ]
  },
  edge: {
    label: "Microsoft Edge",
    extensionsUrl: "edge://extensions",
    steps: [
      "Extract the downloaded ZIP file to a folder on your computer.",
      { text: "Open ", link: "edge://extensions", after: "." },
      "Turn Developer mode ON (bottom-left corner).",
      "Click Load unpacked (top-center of the page).",
      "Browse to the extracted SalesIntel folder.",
      "Select the folder and click Select Folder."
    ]
  }
};

const CopyChip = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    Utils.copyToClipboard(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      type="button"
      className={`copyChip ${copied ? "copied" : ""}`}
      onClick={copy}
      title="Copy and paste into a new browser tab"
    >
      <span>{text}</span>
      <i className="material-icons">{copied ? "check" : "content_copy"}</i>
    </button>
  );
};

const Step = ({ item }) => {
  if (typeof item === "string") return <li>{item}</li>;
  return (
    <li>
      {item.text}
      <CopyChip text={item.link} />
      {item.after}
    </li>
  );
};

const LinkedInInstallPopup = observer(() => {
  const show = linkedInInstallPopupState.showPopup;
  // Keep the drawer mounted through the slide-out so closing animates too.
  const [mounted, setMounted] = useState(show);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (show) {
      setMounted(true);
      const id = requestAnimationFrame(() => setOpen(true));
      return () => cancelAnimationFrame(id);
    }
    setOpen(false);
    const id = setTimeout(() => setMounted(false), 260);
    return () => clearTimeout(id);
  }, [show]);

  if (!mounted) return null;

  const closeCbk = () => linkedInInstallPopupState.setShowPopup(false);
  const browser = getBrowser();
  const installed = userDetail.linkedInConnectpluginInstalled;

  const downloadExtension = () => {
    window.open(LINKEDIN_PLUGIN_ZIP_DOWNLOAD_URL, "_blank");
  };

  const renderBody = () => {
    if (installed) {
      return (
        <div className="linkedInInstallInstalled">
          <i className="material-icons">check_circle</i>
          <div className="notification">
            Extension installed successfully. Click Connect LinkedIn to
            authorize your account.
          </div>
        </div>
      );
    }

    if (browser === "other") {
      return (
        <div className="notification">
          LinkedIn integration is currently supported only on Google Chrome and
          Microsoft Edge. Please open SalesIntel in one of these browsers to
          continue.
        </div>
      );
    }

    const config = INSTALL_STEPS[browser];
    return (
      <>
        <div className="notification">
          To connect your LinkedIn account, first install the SalesIntel browser
          extension.
        </div>
        <button className="downloadExtensionBtn" onClick={downloadExtension}>
          <i className="material-icons">file_download</i>
          Download Extension
        </button>
        <div className="helperText">
          Clicking Download Extension will download the SalesIntel extension as a
          ZIP file.
        </div>
        <div className="installSteps">
          <div className="installStepsTitle">
            Install the Extension &mdash; {config.label}
          </div>
          <ol>
            {config.steps.map((item, idx) => (
              <Step key={idx} item={item} />
            ))}
          </ol>
        </div>
        <div className="nextNote">
          After the extension is installed, click Connect LinkedIn and complete
          the LinkedIn authorization process. Once connected, you can start
          using LinkedIn automation.
        </div>
      </>
    );
  };

  return (
    <section className={`linkedInInstallDrawer ${open ? "open" : ""}`}>
      <div className="linkedInInstallOverlay" onClick={closeCbk} />
      <div className="linkedInInstallPanel">
        <div className="linkedInInstallHeader">
          <div className="linkedInInstallHeaderTitle">
            <i className="material-icons headerIcon">extension</i>
            <h4>Connect LinkedIn</h4>
          </div>
          <i className="material-icons closeIcon" onClick={closeCbk}>
            close
          </i>
        </div>
        <div className="linkedInInstallBody">{renderBody()}</div>
      </div>
    </section>
  );
});

const pluginNotInstalled = () => {
  linkedInInstallPopupState.setShowPopup(true);
};

export { pluginNotInstalled, LinkedInInstallPopup, linkedInInstallPopupState };
export default pluginNotInstalled;
